/**
 * Traslados entre tipos de huevo (reclasificación).
 *
 * Reglas:
 * - Acceso gated por código de Super Usuario (server-side también verifica).
 * - Un traslado resta del tipo origen y suma al tipo destino la misma cantidad.
 * - Requiere: fecha, tipo_origen, tipo_destino (distintos), cantidad > 0,
 *   motivo (Reclasificación / Merma / Reubicación / Otro), responsable, y opcional
 *   observaciones.
 * - Aparece en el Historial de Movimientos como 2 rows: "Traslado Salida" y
 *   "Traslado Entrada".
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  ArrowRightLeft, KeyRound, ShieldAlert, RefreshCw, Save, AlertTriangle, CheckCircle2, User, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { verificarSuperCodigo, crearTraslado, getTraslados, getStock } from '../../lib/api';
import { EGG_LABELS } from './constants';
import { formatDate } from '../../lib/dateUtils';

const TIPOS = ['grande', 'mediano', 'pequeño', 'doble', 'sucio', 'casquiado'];
const MOTIVOS = ['Reclasificación', 'Merma', 'Reubicación', 'Otro'];

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('es-ES') : '0');
const todayIso = () => new Date().toISOString().split('T')[0];

const emptyForm = () => ({
  fecha: todayIso(),
  tipo_origen: 'grande',
  tipo_destino: 'mediano',
  cantidad: '',
  motivo: 'Reclasificación',
  responsable: '',
  observaciones: '',
});

export const TrasladosTab = () => {
  // Gate de acceso por Super Usuario
  const [unlocked, setUnlocked] = useState(false);
  const [codigoInput, setCodigoInput] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  // El código lo mantenemos en state para reenviarlo con cada traslado
  const [superCodigo, setSuperCodigo] = useState('');

  const [form, setForm] = useState(emptyForm);
  const [stock, setStock] = useState({});
  const [saving, setSaving] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);

  const loadStock = useCallback(async () => {
    try {
      const r = await getStock();
      const items = r.data?.stock ? Object.entries(r.data.stock) : (Array.isArray(r.data) ? r.data.map((i) => [i.tipo_huevo, i.cantidad_disponible]) : []);
      const map = {};
      items.forEach(([k, v]) => {
        const key = k === 'pequeno' ? 'pequeño' : k;
        map[key] = Number(v || 0);
      });
      setStock(map);
    } catch (err) {
      console.error('Error stock:', err);
    }
  }, []);

  const loadHistorial = useCallback(async () => {
    setLoadingHist(true);
    try {
      const r = await getTraslados({ limit: 100 });
      setHistorial(r.data || []);
    } catch (err) {
      console.error('Error historial traslados:', err);
    } finally {
      setLoadingHist(false);
    }
  }, []);

  useEffect(() => {
    if (unlocked) {
      loadStock();
      loadHistorial();
    }
  }, [unlocked, loadStock, loadHistorial]);

  const handleUnlock = async () => {
    const c = codigoInput.trim();
    if (c.length < 4) { toast.error('Ingrese el código de Super Usuario'); return; }
    setVerifyingCode(true);
    try {
      await verificarSuperCodigo(c);
      setSuperCodigo(c);
      setUnlocked(true);
      setCodigoInput('');
      toast.success('Acceso otorgado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Código incorrecto');
    } finally {
      setVerifyingCode(false);
    }
  };

  // Cálculos derivados
  const stockOrigen = useMemo(() => Number(stock[form.tipo_origen] || 0), [stock, form.tipo_origen]);
  const stockDestino = useMemo(() => Number(stock[form.tipo_destino] || 0), [stock, form.tipo_destino]);
  const cantidadNum = useMemo(() => parseInt(form.cantidad || 0, 10) || 0, [form.cantidad]);
  const excede = cantidadNum > stockOrigen && cantidadNum > 0;
  const mismoTipo = form.tipo_origen === form.tipo_destino;

  const handleSubmit = async () => {
    if (!form.fecha) { toast.error('Seleccione una fecha'); return; }
    if (mismoTipo) { toast.error('Origen y destino no pueden ser el mismo tipo'); return; }
    if (!cantidadNum || cantidadNum <= 0) { toast.error('Cantidad debe ser mayor a 0'); return; }
    if (excede) { toast.error(`No hay suficiente stock en ${form.tipo_origen} (${fmt(stockOrigen)})`); return; }
    if (!form.responsable.trim()) { toast.error('Ingrese el responsable'); return; }

    setSaving(true);
    try {
      const payload = {
        fecha: form.fecha,
        tipo_origen: form.tipo_origen,
        tipo_destino: form.tipo_destino,
        cantidad: cantidadNum,
        motivo: form.motivo,
        responsable: form.responsable.trim(),
        observaciones: form.observaciones?.trim() || null,
        super_codigo: superCodigo,
      };
      await crearTraslado(payload);
      toast.success(
        `Traslado registrado · ${fmt(cantidadNum)} ${EGG_LABELS[form.tipo_origen === 'pequeño' ? 'pequeno' : form.tipo_origen]} → ${EGG_LABELS[form.tipo_destino === 'pequeño' ? 'pequeno' : form.tipo_destino]}`,
      );
      setForm({ ...emptyForm(), fecha: form.fecha, responsable: form.responsable });
      await Promise.all([loadStock(), loadHistorial()]);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Error registrando traslado');
      toast.error(msg);
      // Si el código expiró (403), forzamos re-lock
      if (err.response?.status === 403) {
        setUnlocked(false);
        setSuperCodigo('');
      }
    } finally {
      setSaving(false);
    }
  };

  // ============== VISTA CON GATE ==============
  if (!unlocked) {
    return (
      <Card data-testid="traslados-locked">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-primary" />
            Traslados entre tipos de huevo
          </CardTitle>
        </CardHeader>
        <CardContent className="max-w-md mx-auto py-8 space-y-4">
          <div className="flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-md p-2.5">
            <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-amber-900 dark:text-amber-200">
              Esta pestaña permite <strong>reclasificar</strong> huevos entre tipos (ej. Grandes → Medianos).
              Solo Super Usuario puede acceder. Ingrese el código para continuar.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="traslados-code-input" className="flex items-center gap-1.5">
              <KeyRound className="w-4 h-4" />
              Código de Super Usuario
            </Label>
            <Input
              id="traslados-code-input"
              type="password"
              value={codigoInput}
              onChange={(e) => setCodigoInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !verifyingCode) handleUnlock(); }}
              placeholder="Ingrese el código"
              className="h-11 font-mono"
              autoFocus
              data-testid="traslados-code-input"
            />
          </div>
          <Button
            onClick={handleUnlock}
            disabled={verifyingCode || codigoInput.trim().length < 4}
            className="w-full h-11"
            data-testid="traslados-unlock-btn"
          >
            {verifyingCode ? 'Verificando…' : 'Desbloquear acceso'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ============== VISTA DESBLOQUEADA ==============
  return (
    <div className="space-y-6" data-testid="traslados-wrapper">
      {/* Form */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-primary" />
              Nuevo Traslado
            </CardTitle>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setUnlocked(false); setSuperCodigo(''); }}
              className="text-xs text-muted-foreground"
              data-testid="traslados-lock-btn"
            >
              🔒 Bloquear
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label>Fecha</Label>
              <Input
                type="date"
                value={form.fecha}
                onChange={(e) => setForm((p) => ({ ...p, fecha: e.target.value }))}
                max={todayIso()}
                className="h-10"
                data-testid="traslado-fecha"
              />
            </div>
            <div className="space-y-1">
              <Label>Motivo</Label>
              <Select value={form.motivo} onValueChange={(v) => setForm((p) => ({ ...p, motivo: v }))}>
                <SelectTrigger className="h-10" data-testid="traslado-motivo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOTIVOS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Responsable</Label>
              <Input
                value={form.responsable}
                onChange={(e) => setForm((p) => ({ ...p, responsable: e.target.value }))}
                placeholder="Nombre de la persona responsable"
                className="h-10"
                maxLength={100}
                data-testid="traslado-responsable"
              />
            </div>
          </div>

          {/* Origen → Destino */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_1fr] gap-3 md:gap-4 items-end">
            <div className="space-y-1">
              <Label>Desde (origen)</Label>
              <Select value={form.tipo_origen} onValueChange={(v) => setForm((p) => ({ ...p, tipo_origen: v }))}>
                <SelectTrigger className="h-10" data-testid="traslado-origen"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t} value={t}>{EGG_LABELS[t === 'pequeño' ? 'pequeno' : t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground tabular-nums">
                Stock actual: <strong>{fmt(stockOrigen)}</strong>
              </p>
            </div>

            <div className="flex flex-col items-center justify-center pb-6">
              <ArrowRightLeft className="w-5 h-5 text-primary" />
            </div>

            <div className="space-y-1">
              <Label>Hacia (destino)</Label>
              <Select value={form.tipo_destino} onValueChange={(v) => setForm((p) => ({ ...p, tipo_destino: v }))}>
                <SelectTrigger className={`h-10 ${mismoTipo ? 'border-red-500' : ''}`} data-testid="traslado-destino">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t} value={t}>{EGG_LABELS[t === 'pequeño' ? 'pequeno' : t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground tabular-nums">
                Stock actual: <strong>{fmt(stockDestino)}</strong>
              </p>
            </div>

            <div className="space-y-1">
              <Label>Cantidad a trasladar</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.cantidad}
                onChange={(e) => setForm((p) => ({ ...p, cantidad: e.target.value.replace(/[^0-9]/g, '') }))}
                placeholder="0"
                className={`h-10 text-right tabular-nums ${excede ? 'border-red-500' : ''}`}
                data-testid="traslado-cantidad"
              />
              {excede && (
                <p className="text-[11px] text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Excede stock disponible
                </p>
              )}
              {mismoTipo && (
                <p className="text-[11px] text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Origen y destino iguales
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label>Observaciones (opcional)</Label>
            <Textarea
              value={form.observaciones}
              onChange={(e) => setForm((p) => ({ ...p, observaciones: e.target.value }))}
              placeholder="Justificación adicional, contexto, etc."
              className="min-h-[70px]"
              maxLength={500}
              data-testid="traslado-observaciones"
            />
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            {cantidadNum > 0 && !mismoTipo && !excede ? (
              <Badge variant="outline" className="gap-1 text-green-700 border-green-300 bg-green-50">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Listo para trasladar: {fmt(cantidadNum)} unidades
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground">Complete los campos requeridos</span>
            )}
            <Button
              onClick={handleSubmit}
              disabled={saving || excede || mismoTipo || !cantidadNum || !form.responsable.trim()}
              data-testid="traslado-guardar"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Registrando…' : 'Registrar traslado'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Historial */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Historial de Traslados
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-normal">
                {historial.length} traslado(s)
              </Badge>
              <Button
                size="sm"
                variant="ghost"
                onClick={loadHistorial}
                disabled={loadingHist}
                className="h-7"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingHist ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {historial.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <ArrowRightLeft className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{loadingHist ? 'Cargando…' : 'No hay traslados registrados aún'}</p>
            </div>
          ) : (
            <Table wrapperClassName="max-h-[calc(100vh-500px)] rounded-md border border-border/40" data-testid="tabla-historial-traslados">
              <TableHeader className="sticky top-0 z-30 bg-card shadow-[0_2px_4px_-1px_rgba(0,0,0,0.08)] [&_tr]:border-b-0 [&_th]:border-b [&_th]:border-border">
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead></TableHead>
                  <TableHead>Destino</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Responsable</TableHead>
                  <TableHead>Observaciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historial.map((t, idx) => (
                  <TableRow key={`${t.created_at}-${idx}`} data-testid={`traslado-row-${idx}`}>
                    <TableCell className="text-sm">{formatDate(t.fecha)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {EGG_LABELS[t.tipo_origen === 'pequeño' ? 'pequeno' : t.tipo_origen] || t.tipo_origen}
                      </Badge>
                    </TableCell>
                    <TableCell className="p-0 w-6">
                      <ArrowRightLeft className="w-3.5 h-3.5 text-primary" />
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {EGG_LABELS[t.tipo_destino === 'pequeño' ? 'pequeno' : t.tipo_destino] || t.tipo_destino}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmt(t.cantidad)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-normal">{t.motivo || '—'}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3 h-3 text-muted-foreground" />
                        {t.responsable || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[240px] truncate" title={t.observaciones || ''}>
                      {t.observaciones || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TrasladosTab;
